import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { PlatformService } from './platform';

export interface CameraOptions {
  quality?: number; // 0-100, default 90
  allowEditing?: boolean; // default true
  resultType?: CameraResultType; // default Uri
  source?: CameraSource; // default Prompt (asks user to choose camera or gallery)
  width?: number; // max width in pixels
  height?: number; // max height in pixels
  saveToGallery?: boolean; // default false
}

export interface CameraResult {
  success: boolean;
  photo?: Photo;
  file?: File;
  error?: string;
  permissionDenied?: boolean;
}

export interface PermissionStatus {
  granted: boolean;
  denied: boolean;
  canRequest: boolean;
  needsSettingsRedirect: boolean;
}

export class CameraService {
  /**
   * Default camera options optimized for profile pictures
   */
  private static readonly DEFAULT_PROFILE_OPTIONS: CameraOptions = {
    quality: 90,
    allowEditing: false, // Disable editing for direct capture
    resultType: CameraResultType.Uri,
    source: CameraSource.Prompt, // Let user choose camera or gallery
    width: 800,
    height: 800,
    saveToGallery: false
  };

  /**
   * Default camera options optimized for document capture
   */
  private static readonly DEFAULT_DOCUMENT_OPTIONS: CameraOptions = {
    quality: 100,
    allowEditing: true,
    resultType: CameraResultType.Uri,
    source: CameraSource.Camera, // Force camera for documents
    width: 1200,
    height: 1600,
    saveToGallery: false
  };

  /**
   * Check detailed camera permission status
   */
  static async getPermissionStatus(): Promise<PermissionStatus> {
    if (!PlatformService.isNative()) {
      return {
        granted: false,
        denied: false,
        canRequest: false,
        needsSettingsRedirect: false
      };
    }

    try {
      const permissions = await Camera.checkPermissions();
      
      return {
        granted: permissions.camera === 'granted' && permissions.photos === 'granted',
        denied: permissions.camera === 'denied' || permissions.photos === 'denied',
        canRequest: permissions.camera === 'prompt' || permissions.photos === 'prompt',
        needsSettingsRedirect: permissions.camera === 'denied' || permissions.photos === 'denied'
      };
    } catch (error) {
      console.warn('Failed to check camera permissions:', error);
      return {
        granted: false,
        denied: true,
        canRequest: false,
        needsSettingsRedirect: false
      };
    }
  }

  /**
   * Check if camera is available on the current platform
   */
  static async isCameraAvailable(): Promise<boolean> {
    const status = await this.getPermissionStatus();
    return status.granted || status.canRequest;
  }

  /**
   * Request camera permissions
   */
  static async requestCameraPermissions(): Promise<boolean> {
    if (!PlatformService.isNative()) {
      return false;
    }

    try {
      const permissions = await Camera.requestPermissions({ permissions: ['camera', 'photos'] });
      return permissions.camera === 'granted' && permissions.photos === 'granted';
    } catch (error) {
      console.error('Failed to request camera permissions:', error);
      return false;
    }
  }

  /**
   * Open device settings for camera permissions
   * Note: This is a simplified implementation. For production, consider using AppLauncher plugin
   */
  static async openSettings(): Promise<void> {
    if (!PlatformService.isNative()) {
      return;
    }

    try {
      // For now, we'll use window.open as a fallback
      // In a production app, you might want to install @capacitor/app-launcher
      const settingsUrl = PlatformService.isIOS() ? 'app-settings:' : 'market://details?id=com.insightflow.app';
      window.open(settingsUrl, '_system');
    } catch (error) {
      console.error('Failed to open settings:', error);
    }
  }

  /**
   * Take a photo using the device camera or select from gallery
   */
  static async takePhoto(options?: CameraOptions): Promise<CameraResult> {
    try {
      // Check if camera is available
      if (!PlatformService.isNative()) {
        return {
          success: false,
          error: 'Camera is only available on mobile devices'
        };
      }

      // Check and request permissions if needed
      const permissionStatus = await this.getPermissionStatus();
      
      if (!permissionStatus.granted) {
        if (permissionStatus.canRequest) {
          const hasPermissions = await this.requestCameraPermissions();
          if (!hasPermissions) {
            return {
              success: false,
              error: 'Camera permissions are required to take photos',
              permissionDenied: true
            };
          }
        } else if (permissionStatus.needsSettingsRedirect) {
          return {
            success: false,
            error: 'Camera permissions are required. Please enable them in Settings.',
            permissionDenied: true
          };
        }
      }

      // Merge with default options and ensure required fields are present
      const mergedOptions = {
        ...this.DEFAULT_PROFILE_OPTIONS,
        ...options
      };

      const cameraOptions = {
        quality: mergedOptions.quality!,
        allowEditing: mergedOptions.allowEditing!,
        resultType: mergedOptions.resultType!,
        source: mergedOptions.source!,
        width: mergedOptions.width,
        height: mergedOptions.height,
        saveToGallery: mergedOptions.saveToGallery
      };

      console.log('Taking photo with options:', cameraOptions);

      // Take photo using Capacitor Camera
      const photo = await Camera.getPhoto(cameraOptions);
      
      console.log('Photo captured:', {
        webPath: photo.webPath,
        hasBase64: !!photo.base64String,
        format: photo.format,
        dataUrl: photo.dataUrl ? 'present' : 'missing'
      });

      // Convert to File using our improved conversion method
      let file: File | undefined;
      try {
        const convertedFile = await this.photoToFile(photo);
        if (!convertedFile) {
          console.warn('Failed to convert photo to file, trying alternative method');
          // Try alternative conversion if primary method fails
          const altFile = await this.alternativePhotoToFile(photo);
          file = altFile || undefined;
        } else {
          file = convertedFile;
        }
      } catch (error) {
        console.error('Error converting photo to file:', error);
        // Try alternative conversion method
        try {
          const altFile = await this.alternativePhotoToFile(photo);
          file = altFile || undefined;
        } catch (altError) {
          console.error('Alternative photo conversion also failed:', altError);
          file = undefined;
        }
      }

      if (!file) {
        return {
          success: false,
          error: 'Failed to process the captured photo. Please try again.'
        };
      }

      console.log('File created successfully:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      return {
        success: true,
        photo,
        file
      };
    } catch (error: any) {
      console.error('Camera error:', error);
      
      // Handle user cancellation
      if (error.message && error.message.includes('cancelled')) {
        return {
          success: false,
          error: 'Photo capture was cancelled'
        };
      }

      return {
        success: false,
        error: error.message || 'Failed to capture photo'
      };
    }
  }

  /**
   * Convenience method for taking profile pictures
   */
  static async takeProfilePicture(): Promise<CameraResult> {
    return this.takePhoto(this.DEFAULT_PROFILE_OPTIONS);
  }

  /**
   * Convenience method for capturing documents
   */
  static async captureDocument(): Promise<CameraResult> {
    return this.takePhoto(this.DEFAULT_DOCUMENT_OPTIONS);
  }

  /**
   * Take a photo specifically from camera (no gallery option)
   */
  static async takeCameraPhoto(options?: Omit<CameraOptions, 'source'>): Promise<CameraResult> {
    return this.takePhoto({
      ...options,
      source: CameraSource.Camera
    });
  }

  /**
   * Take a direct camera photo for profile pictures (no editing, no gallery)
   */
  static async takeDirectCameraPhoto(): Promise<CameraResult> {
    return this.takePhoto({
      quality: 85, // Slightly lower quality for faster processing
      allowEditing: false, // Force no editing
      resultType: CameraResultType.DataUrl, // Use DataUrl for immediate processing
      source: CameraSource.Camera, // Force camera only
      width: 800,
      height: 800,
      saveToGallery: false
    });
  }

  /**
   * Select a photo from gallery only
   */
  static async selectFromGallery(options?: Omit<CameraOptions, 'source'>): Promise<CameraResult> {
    return this.takePhoto({
      ...options,
      source: CameraSource.Photos
    });
  }

  /**
   * Convert photo data URL to File
   */
  static async photoToFile(photo: Photo, filename?: string): Promise<File | null> {
    try {
      if (!photo.webPath && !photo.base64String && !photo.dataUrl) {
        console.warn('No usable photo data found');
        return null;
      }

      let blob: Blob;
      const fileName = filename || `photo-${Date.now()}.jpg`;

      // Try webPath first (most reliable)
      if (photo.webPath) {
        console.log('Converting from webPath:', photo.webPath);
        const response = await fetch(photo.webPath);
        if (!response.ok) {
          throw new Error(`Failed to fetch photo: ${response.status}`);
        }
        blob = await response.blob();
      } 
      // Try dataUrl second (Capacitor 7 often provides this)
      else if (photo.dataUrl) {
        console.log('Converting from dataUrl');
        const response = await fetch(photo.dataUrl);
        blob = await response.blob();
      }
      // Try base64String last (fallback)
      else if (photo.base64String) {
        console.log('Converting from base64String');
        const base64Data = photo.base64String;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        
        const byteArray = new Uint8Array(byteNumbers);
        blob = new Blob([byteArray], { type: 'image/jpeg' });
      } else {
        return null;
      }

      // Validate blob
      if (!blob || blob.size === 0) {
        throw new Error('Generated blob is empty');
      }

      console.log('Blob created successfully:', {
        size: blob.size,
        type: blob.type
      });

      return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
    } catch (error) {
      console.error('Failed to convert photo to file:', error);
      return null;
    }
  }

  /**
   * Alternative photo to file conversion method for Capacitor 7
   */
  private static async alternativePhotoToFile(photo: Photo, filename?: string): Promise<File | null> {
    try {
      console.log('Trying alternative photo conversion method');
      
      const fileName = filename || `photo-${Date.now()}.jpg`;
      
      // For Capacitor 7, sometimes we need to handle the photo differently
      if (photo.webPath) {
        // Try using XMLHttpRequest instead of fetch
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', photo.webPath!, true);
          xhr.responseType = 'blob';
          
          xhr.onload = () => {
            if (xhr.status === 200) {
              const blob = xhr.response;
              if (blob && blob.size > 0) {
                const file = new File([blob], fileName, { type: 'image/jpeg' });
                console.log('Alternative conversion successful:', {
                  name: file.name,
                  size: file.size,
                  type: file.type
                });
                resolve(file);
              } else {
                reject(new Error('Empty blob from alternative method'));
              }
            } else {
              reject(new Error(`XHR failed with status: ${xhr.status}`));
            }
          };
          
          xhr.onerror = () => reject(new Error('XHR request failed'));
          xhr.send();
        });
      }
      
      // If we have base64, try a more robust base64 conversion
      if (photo.base64String) {
        const base64 = photo.base64String;
        // Remove data URL prefix if present
        const base64Data = base64.replace(/^data:image\/[a-z]+;base64,/, '');
        
        // Convert to binary string
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        const blob = new Blob([bytes], { type: 'image/jpeg' });
        
        if (blob.size > 0) {
          return new File([blob], fileName, { type: 'image/jpeg' });
        }
      }
      
      return null;
    } catch (error) {
      console.error('Alternative photo conversion failed:', error);
      return null;
    }
  }

  /**
   * Get optimized camera options for different use cases
   */
  static getOptionsForUseCase(useCase: 'profile' | 'document' | 'sharing'): CameraOptions {
    switch (useCase) {
      case 'profile':
        return { ...this.DEFAULT_PROFILE_OPTIONS };
      case 'document':
        return { ...this.DEFAULT_DOCUMENT_OPTIONS };
      case 'sharing':
        return {
          quality: 85,
          allowEditing: true,
          resultType: CameraResultType.Uri,
          source: CameraSource.Prompt,
          width: 1080,
          height: 1080,
          saveToGallery: true // Save sharing photos to gallery
        };
      default:
        return { ...this.DEFAULT_PROFILE_OPTIONS };
    }
  }
} 